import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, CheckCircle2, X, MapPin, User, Phone, Mail, MessageSquare } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const WEEKDAY_LABELS = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];
const MONTH_NAMES = ["Január", "Február", "Marec", "Apríl", "Máj", "Jún", "Júl", "August", "September", "Október", "November", "December"];

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function humanDate(iso) {
  const [y, m, day] = iso.split("-").map(Number);
  const d = new Date(y, m - 1, day);
  const wd = ["Nedeľa", "Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota"][d.getDay()];
  return `${wd}, ${day}. ${MONTH_NAMES[m - 1]} ${y}`;
}

/**
 * BookingCalendar — public landing widget.
 *
 * 1. Fetches available 60-min slots for the next 30 days.
 * 2. Renders a compact month calendar; dates with available slots are highlighted.
 * 3. Click a date → time-slot picker.
 * 4. Click a slot → form (name, phone, email, address, message) → confirmation.
 */
export default function BookingCalendar() {
  const [slots, setSlots] = useState([]); // [{id, date, start_time}]
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null); // full slot obj when modal open
  const [confirmed, setConfirmed] = useState(null); // {date, time}
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const rangeEnd = useMemo(() => {
    const t = new Date(today);
    t.setDate(t.getDate() + 30);
    return t;
  }, [today]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/booking/available`, {
          params: { from_date: isoDate(today), to_date: isoDate(rangeEnd) },
        });
        if (alive) setSlots(data.slots || []);
      } catch {
        if (alive) setError("Nepodarilo sa načítať voľné termíny. Skúste to o chvíľu.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [today, rangeEnd]);

  // Group slots by date for O(1) lookup
  const slotsByDate = useMemo(() => {
    const map = {};
    for (const s of slots) {
      (map[s.date] ||= []).push(s);
    }
    // Sort times within each date
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return map;
  }, [slots]);

  // Build a 30-day list of calendar cells
  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const iso = isoDate(d);
      out.push({
        iso,
        day: d.getDate(),
        month: d.getMonth(),
        weekday: (d.getDay() + 6) % 7, // 0=Mon
        count: (slotsByDate[iso] || []).length,
      });
    }
    return out;
  }, [today, slotsByDate]);

  const openSlotForm = (slot) => {
    setSelectedSlot(slot);
    setError("");
  };
  const closeForm = () => {
    setSelectedSlot(null);
    setForm({ name: "", phone: "", email: "", address: "", message: "" });
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.address.trim()) {
      setError("Vyplňte prosím meno, telefón, email a adresu stavby.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/booking/book/${selectedSlot.id}`, form);
      setConfirmed({ date: selectedSlot.date, time: selectedSlot.start_time });
      // Remove booked slot from local state
      setSlots((prev) => prev.filter((s) => s.id !== selectedSlot.id));
      setSelectedSlot(null);
      setForm({ name: "", phone: "", email: "", address: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Rezerváciu sa nepodarilo uložiť. Skúste iný termín.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="obhliadka"
      className="py-20 bg-gradient-to-b from-[#0a0a0a] via-[#0f0f0f] to-[#0a0a0a] relative overflow-hidden"
      data-testid="booking-calendar-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 relative">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-black mb-3">
            Rezervácia obhliadky · Zdarma · 60 minút · Nezáväzne
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
            Vyberte si <span className="text-[#00E5FF] neon-cyan">termín</span>
            <span className="text-[#FF5E00]">.</span>
          </h2>
          <p className="text-zinc-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">
            Ján príde priamo k vám. Poobzeráme priestor, prejdeme si vaše predstavy
            a do 24 h vám pošleme cenovú ponuku. Bez tlaku, bez opakovaných telefonátov.
          </p>
        </div>

        {confirmed && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 p-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-center"
            data-testid="booking-confirmed"
          >
            <CheckCircle2 size={36} className="text-emerald-400 mx-auto mb-3" />
            <h3 className="text-2xl font-black text-white mb-1">Termín potvrdený!</h3>
            <p className="text-zinc-300">
              {humanDate(confirmed.date)} · <strong>{confirmed.time}</strong>
            </p>
            <p className="text-sm text-zinc-400 mt-2">
              Potvrdenie sme vám poslali na email. Zavoláme 1 h pred obhliadkou.
            </p>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
          {/* Calendar grid */}
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/40 p-5" data-testid="booking-calendar-grid">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mono mb-4">
              <Calendar size={14} className="text-[#00E5FF]" />
              Najbližších 30 dní
            </div>

            {loading ? (
              <div className="py-12 text-center text-sm text-zinc-500">Načítavam voľné termíny…</div>
            ) : (
              <>
                {/* Weekday header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {WEEKDAY_LABELS.map((w) => (
                    <div key={w} className="text-[10px] mono uppercase text-center text-zinc-600">{w}</div>
                  ))}
                </div>
                {/* Day cells — leading blanks to align weekday of first cell */}
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: days[0]?.weekday || 0 }).map((_, i) => (
                    <div key={`b${i}`} className="aspect-square" />
                  ))}
                  {days.map((d) => {
                    const has = d.count > 0;
                    const isSel = selectedDate === d.iso;
                    return (
                      <button
                        key={d.iso}
                        onClick={() => setSelectedDate(has ? d.iso : null)}
                        disabled={!has}
                        className={`aspect-square rounded-lg text-sm transition-all relative ${
                          isSel
                            ? "bg-[#00E5FF] text-black font-black"
                            : has
                              ? "bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-white hover:bg-[#00E5FF]/20"
                              : "bg-white/[0.02] text-zinc-600 cursor-not-allowed"
                        }`}
                        data-testid={`booking-day-${d.iso}`}
                      >
                        <span className="block leading-none">{d.day}</span>
                        {has && !isSel && (
                          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[9px] text-[#00E5FF] font-black">
                            {d.count}×
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 text-[10px] mono uppercase text-zinc-500 mt-4">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-[#00E5FF]/40 border border-[#00E5FF]/40" />
                    Voľné
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-white/5" />
                    Bez termínov
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Time-slot picker */}
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/40 p-5" data-testid="booking-slots-panel">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mono mb-4">
              <Clock size={14} className="text-[#FF5E00]" />
              {selectedDate ? "Voľné časy" : "Vyberte deň"}
            </div>
            {!selectedDate && (
              <div className="py-12 text-center text-sm text-zinc-500">
                Kliknite na deň v kalendári vľavo.
              </div>
            )}
            {selectedDate && (
              <div>
                <div className="text-white font-bold mb-4">{humanDate(selectedDate)}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(slotsByDate[selectedDate] || []).map((s) => (
                    <button
                      key={s.id}
                      onClick={() => openSlotForm(s)}
                      className="px-4 py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-[#00E5FF]/10 hover:border-[#00E5FF]/40 text-white font-medium text-sm transition-all"
                      data-testid={`booking-slot-${s.id}`}
                    >
                      {s.start_time}
                    </button>
                  ))}
                </div>
                {(slotsByDate[selectedDate] || []).length === 0 && (
                  <p className="text-sm text-zinc-500 mt-4">Žiadne voľné termíny.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {!confirmed && !loading && slots.length === 0 && !error && (
          <div className="mt-6 p-5 rounded-2xl border border-amber-500/20 bg-amber-500/5 text-sm text-amber-200 text-center" data-testid="booking-empty">
            Momentálne nie sú vypísané voľné termíny. Zavolajte nám prosím na <a href="tel:0907649964" className="underline font-bold">0907 64 99 64</a> a dohodneme si obhliadku ručne.
          </div>
        )}
        {error && !selectedSlot && (
          <p className="text-sm text-red-400 text-center mt-4" data-testid="booking-error">{error}</p>
        )}
      </div>

      {/* Booking modal */}
      <AnimatePresence>
        {selectedSlot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998] flex items-center justify-center p-4"
            onClick={closeForm}
            data-testid="booking-modal"
          >
            <motion.form
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={submit}
              className="w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-white/10 flex items-start justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-black">
                    Rezervácia obhliadky
                  </div>
                  <h3 className="text-xl font-black text-white mt-1">
                    {humanDate(selectedSlot.date)}
                  </h3>
                  <p className="text-sm text-zinc-400 mt-0.5">
                    Čas: <strong className="text-white">{selectedSlot.start_time}</strong> · trvanie 60 min
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeForm}
                  className="text-zinc-500 hover:text-white p-1"
                  data-testid="booking-modal-close"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 space-y-3">
                <Field icon={User} label="Meno a priezvisko" testid="booking-name">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                    data-testid="booking-name-input"
                  />
                </Field>
                <Field icon={Phone} label="Telefón" testid="booking-phone">
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="0907 123 456"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                    data-testid="booking-phone-input"
                  />
                </Field>
                <Field icon={Mail} label="Email" testid="booking-email">
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="vas@email.sk"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                    data-testid="booking-email-input"
                  />
                </Field>
                <Field icon={MapPin} label="Adresa stavby" testid="booking-address">
                  <input
                    type="text"
                    required
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Ulica, číslo, mesto"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                    data-testid="booking-address-input"
                  />
                </Field>
                <Field icon={MessageSquare} label="Poznámka (nepovinné)" testid="booking-message">
                  <textarea
                    rows={2}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Napr. typ služby, veľkosť projektu…"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00E5FF] resize-none"
                    data-testid="booking-message-input"
                  />
                </Field>
                {error && <p className="text-xs text-red-400" data-testid="booking-form-error">{error}</p>}
              </div>
              <div className="p-6 pt-0 flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-300 text-sm font-medium border border-white/10"
                  data-testid="booking-cancel-btn"
                >
                  Zrušiť
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl bg-[#00E5FF] hover:brightness-110 text-black text-sm font-black disabled:opacity-60"
                  data-testid="booking-submit-btn"
                >
                  {submitting ? "Odosielam…" : "Záväzne rezervovať"}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Field({ icon: Icon, label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="flex items-center gap-2 text-[11px] uppercase mono tracking-widest text-zinc-500 mb-1.5">
        <Icon size={11} className="text-[#00E5FF]" />
        {label}
      </span>
      {children}
    </label>
  );
}
